/* eslint-disable react/no-array-index-key */
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ASSETS } from '../../../../../../../assets';
import {
  MultipleExperienceTitle,
  ExperienceTitle,
  TimelineBoldText,
  TimelineContainerItemLogo,
  TimelineContainerItemLogoWrapper,
  TimelineExperienceText, ExperienceLogoDivider, TimeLineCompanyContainer, RoleDecorator,
} from '../../timeline.styled';
import { createExperienceTimelineRange } from '../../timeline.utils';
import { ExperienceTag } from '../experience-item-tags/experience-item-tags';
import { ExperienceTagType } from '../experience-item-tags/experience-item-tags.enums';
import STRING_KEYS from '../../../../../../../language/keys';
import ExperienceTags from '../experience-item-tags/experience-item-tags.component';
import { AvatarVariants } from '../../../../../../../components/Common/avatar-with-fallback/avatar-variant.enum';
import { RoleInformation } from '../../../../../../../@types/match';
import { PromotionBadge } from './promotion-badge/promotion-badge.component';
import {
  CompanyIcon,
  CompanyName,
  Container, MultipleExperienceContainer,
  PromotionBadgeWrapper,
  Row,
} from './experience-container-item.styled';
import { ExperienceContainerItemProps } from './experience-container-item';

const isPromoted = (role: RoleInformation, prevRole: RoleInformation) => {
  if (
    !role ||
    !prevRole ||
    role.seniorityLevel == null ||
    prevRole.seniorityLevel == null
  ) {
    return false;
  }

  return role.seniorityLevel > prevRole.seniorityLevel;
};

const ExperienceContainerItem = ({
  experienceItem,
  clickHandler,
  viewChangeHandler,
  isLastItem,
}: ExperienceContainerItemProps): JSX.Element => {
  const { t: translate } = useTranslation();

  const talentSkills = useMemo<ExperienceTag[]>(() => {
    return experienceItem.talentSkills?.map((skill) => ({
      text: skill.skillName,
      type: ExperienceTagType.Skill,
      askedByUser: !!skill.askedByUser,
      tagInfo: skill.askedByUser ? translate(STRING_KEYS.MATCH_CARD.EXPERIENCE.SKILL_ASKED_BY_USER_TOOLTIP) : translate(STRING_KEYS.MATCH_CARD.EXPERIENCE.SKILL_TOOLTIP),
    })) || [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [experienceItem.talentSkills]);

  const industries = useMemo<ExperienceTag[]>(() => {
    return experienceItem.industries?.map((industry) => ({
      text: industry.value,
      type: ExperienceTagType.Industry,
      askedByUser: industry.askedByUser,
      tagInfo: translate(STRING_KEYS.MATCH_CARD.EXPERIENCE.INDUSTRY_TOOLTIP),
    })) || [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [experienceItem.industries]);

  const skillsAndIndustries = [...talentSkills, ...industries];

  if (experienceItem.companySizeTier) {
    skillsAndIndustries.unshift({
      text: translate(STRING_KEYS.COMPANY_SIZE[experienceItem.companySizeTier.value]),
      type: ExperienceTagType.CompanySize,
      subtype: experienceItem.companySizeTier.value,
      askedByUser: !!experienceItem.companySizeTier.askedByUser,
      tagInfo: translate(STRING_KEYS.MATCH_CARD.EXPERIENCE.COMPANY_SIZE_TOOLTIP),
    });
  }

  const renderLogo = (isMultipleExperience?: boolean) => {
    return (
      <TimelineContainerItemLogoWrapper>
        <TimelineContainerItemLogo
          variant={ AvatarVariants.Circular }
          logo={ experienceItem.companyLogo }
          title={ experienceItem.companyName }
          fallbackImage={ ASSETS.EXPERIENCE_FALLBACK }
        />
        { (
          !isLastItem || isMultipleExperience
        ) && <ExperienceLogoDivider /> }
      </TimelineContainerItemLogoWrapper>
    );
  };

  const onCompanyNameClick = () => {
    if (experienceItem.isCompanyEnriched) {
      clickHandler?.();
    }
  };

  const renderCompanyName = () => {
    return (
      <CompanyName
        onClick={ onCompanyNameClick }
        disabled={ !experienceItem.isCompanyEnriched }
      >
        { experienceItem.companyName }
        { experienceItem.isCompanyEnriched ? <CompanyIcon /> : null}
      </CompanyName>
    );
  };

  const renderTags = () => {
    if (skillsAndIndustries.length) {
      return (
        <ExperienceTags
          tags={ skillsAndIndustries.slice(0, 16) }
          viewChangeHandler={ viewChangeHandler }
        />
      );
    }

    return null;
  };

  const renderOne = () => {
    const lastDataObject: RoleInformation = experienceItem.data.length ?
      experienceItem.data[experienceItem.data.length - 1] : {} as RoleInformation;
    const lastDate = lastDataObject && lastDataObject.endDateObject;

    return (
      <Row>
        { renderLogo() }
        <TimeLineCompanyContainer>
          <TimelineExperienceText>
            { createExperienceTimelineRange(experienceItem.data[0].startDateObject, lastDate) }
          </TimelineExperienceText>
          <ExperienceTitle>
            <TimelineBoldText>{ experienceItem.data[0].title }</TimelineBoldText> <span>at <TimelineBoldText>{ renderCompanyName() }</TimelineBoldText></span>
          </ExperienceTitle>
          { renderTags() }
        </TimeLineCompanyContainer>
      </Row>
    );
  };

  const renderNestedRoles = () => {
    const roles = experienceItem.data.slice().reverse();

    return roles.map((role, index) => {
      const prevRole = roles[index + 1];
      const showPromotionBadge = isPromoted(role, prevRole);

      return (
        <Row key={ `${role.title}_${index}` } >
          <TimelineContainerItemLogoWrapper>
            <RoleDecorator isPromoted={ showPromotionBadge } />
            <ExperienceLogoDivider />
          </TimelineContainerItemLogoWrapper>
          <TimeLineCompanyContainer>
            <TimelineExperienceText>
              { createExperienceTimelineRange(role.startDateObject, role.endDateObject) }
            </TimelineExperienceText>
            <ExperienceTitle>
              <TimelineBoldText>
                { role.title }
              </TimelineBoldText>
              { showPromotionBadge && <PromotionBadgeWrapper><PromotionBadge /></PromotionBadgeWrapper> }
            </ExperienceTitle>
          </TimeLineCompanyContainer>
        </Row>
      );
    });
  };

  const renderMultiple = () => {
    const lastDataObject: RoleInformation = experienceItem.data.length ?
      experienceItem.data[experienceItem.data.length - 1] : {} as RoleInformation;
    const lastDate = lastDataObject && lastDataObject.endDateObject;

    return (
      <MultipleExperienceContainer>
        <Row>
          { renderLogo(true) }
          <TimeLineCompanyContainer>
            <TimelineExperienceText>
              { createExperienceTimelineRange(experienceItem.data[0].startDateObject, lastDate) }
            </TimelineExperienceText>
            <MultipleExperienceTitle>
              <TimelineBoldText>
                { renderCompanyName() }
              </TimelineBoldText>
            </MultipleExperienceTitle>
            { renderTags() }
          </TimeLineCompanyContainer>
        </Row>
        { renderNestedRoles() }
      </MultipleExperienceContainer>
    );
  };

  const render = () => {
    if (experienceItem.data.length === 1) {
      return renderOne();
    }

    return renderMultiple();
  };

  return (
    <Container>
      { render() }
    </Container>
  );
};

export default ExperienceContainerItem;
